/**
 * The wire contract between the Livespace host and an iframe plugin.
 *
 * This file is the single source of truth for the protocol. It is imported by
 * BOTH sides — the `plugin-slot` / `plugins` micro-apps and the author-facing
 * SDK bundle — so the two can never drift. Nothing here may import anything
 * outside this file: it has to stay usable from a plain browser bundle with no
 * dependencies.
 *
 * The prose version lives in `micro-apps/plugin-slot/docs/plugin-contract.md`.
 */

/* -------------------------------------------------------------------------- */
/* Message types                                                              */
/* -------------------------------------------------------------------------- */

/** Plugin → host: "my listener is attached, you may start pushing". */
export const MSG_READY = "livespace-plugin:ready"

/** Host → plugin: which mode this embed is. Always the first message after `ready`. */
export const MSG_MODE = "livespace-plugin:mode"

/** Host → plugin: the host object and the viewing user. `view` mode only. */
export const MSG_CONTEXT = "livespace-plugin:context"

/** Plugin → host: "give me a fresh credential triple" (single-use `api_sha`). */
export const MSG_REQUEST_API_CREDENTIALS =
	"livespace-plugin:request-api-credentials"

/** Host → plugin: the answer to one `request-api-credentials`, correlated by `requestId`. */
export const MSG_API_CREDENTIALS = "livespace-plugin:api-credentials"

/**
 * Plugin → host: "give me a plugin subject token" — the bearer a widget sends
 * to its own backend (P2-27). `view` mode only. Unlike the credential triple,
 * a token is good for many calls until it expires, so the SDK caches it.
 */
export const MSG_REQUEST_SESSION_TOKEN =
	"livespace-plugin:request-session-token"

/** Host → plugin: the answer to one `request-session-token`, correlated by `requestId`. */
export const MSG_SESSION_TOKEN = "livespace-plugin:session-token"

/**
 * Host → plugin: the viewer's language, time zone and formats (P2-27/P2-33).
 * Sent in every mode, once after `mode` and again if they change, so a plugin
 * formats dates and amounts the way Livespace does beside it. Older plugins
 * ignore it.
 */
export const MSG_PREFERENCES = "livespace-plugin:preferences"

/** Host → plugin: the stored config. Sent once, then again on every change. */
export const MSG_CONFIG = "livespace-plugin:config"

/** Host → plugin: "describe your config form" (config mode only). */
export const MSG_REQUEST_CONFIG_FORM = "livespace-plugin:request-config-form"

/** Plugin → host: the declared config form. An empty `fields` means "I have no config". */
export const MSG_CONFIG_FORM = "livespace-plugin:config-form"

/** Plugin → host: show a toast in the Livespace chrome. */
export const MSG_NOTIFY = "livespace-plugin:notify"

/**
 * Host → plugin: the embed entered or left fullscreen.
 *
 * Sent whenever the display mode changes, including when the host initiates it,
 * so a plugin can lay itself out for the space it actually has. This is the only
 * one of the three fullscreen messages a plugin should still handle — see the
 * deprecated pair below.
 */
export const MSG_FULLSCREEN_CHANGED = "livespace-plugin:fullscreen-changed"

/**
 * @deprecated Plugin-initiated fullscreen is being withdrawn. The host still
 * answers these, so existing plugins keep working, but new ones must not send
 * them and the SDK deliberately offers no wrapper. React to
 * {@link MSG_FULLSCREEN_CHANGED} instead.
 */
export const MSG_REQUEST_FULLSCREEN = "livespace-plugin:request-fullscreen"

/** @deprecated See {@link MSG_REQUEST_FULLSCREEN}. */
export const MSG_REQUEST_EXIT_FULLSCREEN =
	"livespace-plugin:request-exit-fullscreen"

/**
 * Plugin → host: the size bounds this plugin's modal should respect.
 *
 * Optional, and only a hint: the host starts from its own default, clamps it to
 * these bounds, and then to the space actually available — so a minimum larger
 * than the screen still yields a modal that fits. Send it again to change the
 * bounds; send `{}` to return to the host's default. Older hosts ignore it.
 */
export const MSG_MODAL_SIZE = "livespace-plugin:modal-size"

/**
 * Plugin → host: what this plugin needs to be placed — where it may go, and
 * the least room it is usable in. Sent alongside the config-form answer, which
 * is when the admin screen is asking; a declaration made later is sent at once.
 *
 * Optional, and absence is NOT an error: a plugin that declares nothing is
 * unconstrained, as every plugin written before this was. The admin screen
 * honours `supportedContextTypes` only for a plugin an admin added by URL — one
 * installed from the Catalog goes where its Catalog entry says. Older hosts
 * ignore it.
 */
export const MSG_PLUGIN_METADATA = "livespace-plugin:metadata"

/**
 * Why the host turned down a fullscreen request. Only ever seen by plugins still
 * using the deprecated request messages.
 */
export type PluginFullscreenRejection =
	| "not-enabled"
	| "not-ready"
	| "user-activation-required"
	| "unsupported"

/** Host → plugin, the payload of {@link MSG_FULLSCREEN_CHANGED}. */
export type FullscreenChangedPayload = {
	active: boolean
	rejected?: PluginFullscreenRejection
}

/* -------------------------------------------------------------------------- */
/* Protocol version                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The contract version this build of the protocol speaks. A plugin states the
 * version it was written against in its `ready`; the host shapes everything it
 * sends to match. Bumped only when a payload a plugin receives changes shape —
 * NOT with every SDK release, which is what `sdkVersion` is for.
 *
 *   1  `ready` carries no payload. `context` is `{ <type>: { id, name }, user }`,
 *      and is in practice sent once. Every plugin that does not state a version
 *      is on 1: SDK 1.0.0, a hand-rolled plugin, or one the host only saw load.
 *   2  `ready` carries `{ protocolVersion, sdkVersion }`. The object in
 *      `context` is the host's full description (see `PluginObjectBase`),
 *      falling back to `{ id, name }`, and `context` is re-sent whenever the
 *      object changes.
 *
 * One-way on purpose: Livespace is never self-hosted, so the host is always the
 * newest party and never needs to announce a version back.
 */
export const PROTOCOL_VERSION = 2

/**
 * Plugin → host, with `ready`. Absent on protocol 1.
 *
 * `sdkVersion` is informational — for logs and support. The host decides on
 * `protocolVersion` alone, so a hand-rolled plugin states that and nothing else.
 */
export type ReadyPayload = { protocolVersion: number; sdkVersion?: string }

/**
 * The version to speak to a plugin, from its `ready` payload. Anything that is
 * not a positive integer — including no payload at all — is protocol 1. A
 * version above the host's own can only be a bug (the host is always newest),
 * and is clamped rather than refused, so the plugin still gets the newest shape.
 */
export const resolveProtocolVersion = (payload: unknown): number => {
	const stated =
		payload && typeof payload === "object"
			? (payload as { protocolVersion?: unknown }).protocolVersion
			: undefined
	if (typeof stated !== "number" || !Number.isInteger(stated) || stated < 1) {
		return 1
	}
	return Math.min(stated, PROTOCOL_VERSION)
}

/**
 * Host behaviour: nothing is sent to a plugin before its `ready`. A document
 * that has finished loading without sending one gets this long to do so; after
 * that the host assumes a ready-less plugin — which `ready`'s absence from the
 * early guidance produced — treats it as protocol 1, sends it data anyway and
 * logs a warning. A `ready` arriving later still upgrades it.
 *
 * The SDK sends `ready` from its script tag, before the document can finish
 * loading, so an SDK plugin never reaches this path.
 */
export const READY_GRACE_MS = 1_000

/* -------------------------------------------------------------------------- */
/* Mode                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Why this plugin was embedded.
 *
 *   view     — running on an object profile. Gets a context and a config.
 *   config   — the admin screen is asking for the configuration form. Gets
 *              neither; answer `request-config-form` and stop.
 *   preview  — the admin screen is showing the plugin to whoever is editing it.
 *              There is no object to be about, so there is NO context — but the
 *              config IS delivered, and re-delivered on every edit, so the
 *              preview reflects what the admin is typing.
 */
export type PluginMode = "view" | "config" | "preview"

export type ModePayload = { mode: PluginMode }

/* -------------------------------------------------------------------------- */
/* Context                                                                    */
/* -------------------------------------------------------------------------- */

export type PluginObjectType = "deal" | "company" | "person" | "space"

/*
 * Vocabulary. The CONTEXT is everything a plugin is told about where it is
 * embedded, in two parts:
 *
 *   object  the record being viewed — a deal, company, person or space — under
 *           its type key. It can change while the plugin is mounted (someone
 *           edits the deal), and the host re-sends the context when it does.
 *   viewer  `user`, the logged-in user. Fixed for the life of the page.
 *
 * An object always has `id` and `name`. Everything else is the host's full
 * description of it, present when `version` is: a host that cannot describe
 * the object (an older Livespace) sends `id` and `name` alone. Check
 * `version`, not individual fields.
 */

/**
 * An owner of the object: a user or a team. A string rather than a union, so a
 * new kind of owner is not a type error.
 */
export type PluginObjectOwner = {
	/** `"user"` or `"team"`. */
	type: string
	id: string
	name: string
}

/**
 * A related record, by name only. When the viewer may not see it, `restricted`
 * is true and `name` is null — the record exists, but not for them.
 */
export type PluginObjectLink = { name: string | null; restricted: boolean }

/**
 * A custom field, keyed by its UUID in `customFields`. `type` is the field's
 * kind; `value` follows it, so narrowing on `type` gives the value's shape.
 *
 * Livespace may add kinds. Treat a `type` you do not recognise as opaque —
 * show its `name`, skip its `value` — rather than failing.
 */
export type PluginCustomField = { name: string } & (
	| { type: "text" | "textarea" | "select" | "radio"; value: string }
	| { type: "number" | "integer"; value: number }
	| { type: "checkbox"; value: boolean }
	| { type: "multiselect" | "multicheckbox"; value: string[] }
	/** `YYYY-MM-DD`. */
	| { type: "date"; value: string }
	/** ISO 8601, with offset. */
	| { type: "datetime"; value: string }
)

/** Every custom field kind, and the JS type of its value. */
export const PLUGIN_CUSTOM_FIELD_VALUE_TYPES = {
	text: "string",
	textarea: "string",
	select: "string",
	radio: "string",
	number: "number",
	integer: "number",
	checkbox: "boolean",
	multiselect: "string[]",
	multicheckbox: "string[]",
	date: "string",
	datetime: "string",
} as const satisfies Record<PluginCustomField["type"], string>

/** What every object carries, whatever its type. */
export type PluginObjectBase = {
	id: string
	name: string
	/** Schema version of the full description. Absent: only `id` and `name`. */
	version?: number
	type?: PluginObjectType
	/** ISO 8601 timestamps. */
	createdAt?: string
	modifiedAt?: string
	lastActivityAt?: string | null
	customId?: string | null
	owners?: PluginObjectOwner[]
	/** Names of the groups the object is in. */
	groups?: string[]
	customFields?: Record<string, PluginCustomField>
}

export type PluginDealObject = PluginObjectBase & {
	type?: "deal"
	number?: string | null
	status?: string
	stage?: string | null
	value?: { amount: number; currency: string } | null
	probability?: number | null
	/** `YYYY-MM-DD`. */
	startDate?: string | null
	/** `YYYY-MM-DD`. */
	endDate?: string | null
	source?: string | null
	contact?: PluginObjectLink | null
	company?: PluginObjectLink | null
}

export type PluginCompanyObject = PluginObjectBase & {
	type?: "company"
	taxId?: string | null
	regon?: string | null
	website?: string | null
	clientStatus?: string | null
	source?: string | null
}

export type PluginPersonObject = PluginObjectBase & {
	type?: "person"
	firstName?: string | null
	middleName?: string | null
	lastName?: string | null
	position?: string | null
	source?: string | null
	company?: PluginObjectLink | null
}

export type PluginSpaceObject = PluginObjectBase & {
	type?: "space"
	number?: string | null
	description?: string | null
}

export type PluginObjectByType = {
	deal: PluginDealObject
	company: PluginCompanyObject
	person: PluginPersonObject
	space: PluginSpaceObject
}

/** Any object, whatever its type. */
export type PluginObject = PluginObjectByType[PluginObjectType]

/** @deprecated The `{ id, name }` every object carries — use {@link PluginObjectBase}. */
export type PluginHostObject = { id: string; name: string }

/**
 * Stored configuration values, keyed by the `key` of the config-form field that
 * produced them. Opaque to the host beyond the declared field constraints.
 *
 * NOT SECRET: this payload is delivered to the plugin iframe in the browser of
 * every user who can see the plugin. Never put credentials here — a secret is
 * declared as a {@link ConfigFormSecretField} instead, and never appears in it.
 */
export type PluginConfigValues = Record<string, string | number | boolean>

/**
 * Host → plugin: the context — the object being viewed, and the viewer.
 *
 * Polymorphic on the object's type: exactly one of `deal` / `company` /
 * `person` / `space` is present, and the key name IS the discriminator. (The
 * full description also carries `type`, but the fallback `{ id, name }` does
 * not, so the key is the one to trust.)
 *
 * Sent once the plugin is ready, and AGAIN whenever the object changes. `view`
 * mode only — there is no object in `preview` or `config`.
 */
export type ContextPayload = {
	deal?: PluginDealObject
	company?: PluginCompanyObject
	person?: PluginPersonObject
	space?: PluginSpaceObject
	user: { id: string } | null
}

/* -------------------------------------------------------------------------- */
/* Host page                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Host PAGE → plugin-slot, over the page's event bus — not a message a plugin
 * ever sees. Livespace emits it when the object on the page changes, with the
 * new full description (the same shape as `lsDataManager`'s `pluginContext`)
 * as its payload. plugin-slot re-sends `context` to every iframe plugin showing
 * that object.
 */
export const HOST_EVENT_CONTEXT_UPDATED = "context-updated"

/**
 * Host → plugin: the stored configuration. Its own message rather than a field
 * on `context`, because unlike the context it can CHANGE while the plugin is
 * mounted: the admin screen re-sends it on every edit so a preview updates as
 * the form is filled in. A field on a once-per-mount message could not do that.
 *
 * `null` means never configured, which is not the same as `{}`.
 */
export type ConfigPayload = { config: PluginConfigValues | null }

/* -------------------------------------------------------------------------- */
/* API credentials                                                            */
/* -------------------------------------------------------------------------- */

export type ApiCredentialsErrorCode =
	| "bad_request"
	| "unauthenticated"
	| "forbidden"
	| "not_found"
	| "conflict"
	| "server_error"

export type ApiCredentialsRequestPayload = { requestId: string }

export type ApiCredentialsPayload =
	| {
			requestId: string
			apiKey: string
			apiSha: string
			apiSession: string
			apiBaseUrl: string
	  }
	| { requestId: string; error: ApiCredentialsErrorCode }

/* -------------------------------------------------------------------------- */
/* Session token                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Why the host could not get a token. The credential codes, plus
 * `not_purchased`: the plugin is installed in this tenant but not paid for.
 */
export type SessionTokenErrorCode = ApiCredentialsErrorCode | "not_purchased"

export type SessionTokenRequestPayload = { requestId: string }

/**
 * `token` is a JWT for the plugin's backend to verify; the plugin itself never
 * reads it. `expiresIn` is its lifetime in seconds, counted from when the
 * answer arrives — the SDK never decodes the token to learn it.
 */
export type SessionTokenPayload =
	| { requestId: string; token: string; expiresIn: number }
	| { requestId: string; error: SessionTokenErrorCode }

/* -------------------------------------------------------------------------- */
/* Viewer preferences                                                         */
/* -------------------------------------------------------------------------- */

/** ISO 8601 weekday numbering: 1 is Monday, 7 is Sunday. */
export type IsoWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7

/**
 * How the viewer reads dates, times and amounts in Livespace, normalised so
 * any formatter can use it. A field the host couldn't read is `null`: use your
 * own default for that one.
 */
export type PluginPreferences = {
	/** BCP 47, e.g. `"pl"`, `"en-GB"`: the language Livespace is shown in. */
	language: string | null
	/** IANA, e.g. `"Europe/Warsaw"`: show times in this zone. */
	timeZone: string | null
	formats: {
		/** Unicode/CLDR date pattern, e.g. `"dd.MM.yyyy"`. */
		date: string | null
		/** Unicode/CLDR time pattern, e.g. `"HH:mm"`. */
		time: string | null
		/** Unicode/CLDR pattern for a date with a time. */
		dateTime: string | null
		firstDayOfWeek: IsoWeekday | null
		number: {
			decimalSeparator: string
			/** May be `""`: no grouping. */
			thousandsSeparator: string
			decimalPlaces: number
		} | null
		/** Amounts use `number`'s separators and decimal places, plus this. */
		money: {
			currencySymbol: string
			symbolPosition: "before" | "after"
			/** Whether a space separates the symbol from the amount. */
			symbolSpacing: boolean
		} | null
	}
}

/** `null` when the host has no preferences at all. */
export type PreferencesPayload = { preferences: PluginPreferences | null }

/* -------------------------------------------------------------------------- */
/* Config form                                                                */
/* -------------------------------------------------------------------------- */

/**
 * A field that produces a value in the saved config. Every one carries a `key`.
 *
 * A secret is NOT one of these — see {@link ConfigFormSecretField}. Nothing that
 * walks value fields must ever pick a secret up, or its value would land in the
 * config every viewer's browser receives.
 */
export type ConfigFormValueField =
	| {
			kind: "text"
			key: string
			label: string
			helpText?: string
			required?: boolean
			placeholder?: string
			minLength?: number
			maxLength?: number
			pattern?: string
			default?: string
	  }
	| {
			kind: "number"
			key: string
			label: string
			helpText?: string
			required?: boolean
			min?: number
			max?: number
			step?: number
			default?: number
	  }
	| {
			kind: "textarea"
			key: string
			label: string
			helpText?: string
			required?: boolean
			maxLength?: number
			placeholder?: string
			default?: string
	  }
	| {
			kind: "checkbox"
			key: string
			label: string
			helpText?: string
			default?: boolean
	  }
	| {
			kind: "select"
			key: string
			label: string
			helpText?: string
			required?: boolean
			options: { value: string; label: string }[]
			default?: string
	  }

/**
 * A secret the plugin's backend needs, such as a third-party API key (P2-27).
 *
 * Shares the `key` namespace with value fields but produces NO config value.
 * The admin's browser encrypts what is typed for the plugin's backend, Livespace
 * stores only the ciphertext, and neither the admin nor the plugin's iframe ever
 * reads it back. Hence no `default` and no value constraints: there is nothing
 * Livespace could check them against.
 *
 * A host renders one only when it opted in to secrets when sanitising; any other
 * host drops it, so it can never fall through to an ordinary input.
 */
export type ConfigFormSecretField = {
	kind: "secret"
	key: string
	label: string
	helpText?: string
	required?: boolean
	placeholder?: string
}

/** A node that only shapes the rendered form. Carries no `key` and no value. */
export type ConfigFormLayoutNode =
	| { kind: "section"; title: string; description?: string }
	| { kind: "helpText"; text: string }

export type ConfigFormField =
	| ConfigFormValueField
	| ConfigFormSecretField
	| ConfigFormLayoutNode

export type ConfigFormPayload = { fields: ConfigFormField[] }

// Listed, not inferred from "not a layout node": a kind this release does not
// know — a secret from a newer SDK, say — must not count as a value field.
const VALUE_KINDS: readonly string[] = [
	"text",
	"number",
	"textarea",
	"checkbox",
	"select",
] satisfies ConfigFormValueField["kind"][]

/** Narrows to the fields that contribute a value to the saved config. */
export const isValueField = (
	field: ConfigFormField,
): field is ConfigFormValueField => VALUE_KINDS.includes(field.kind)

/** Narrows to secret fields, which never contribute to the saved config. */
export const isSecretField = (
	field: ConfigFormField,
): field is ConfigFormSecretField => field.kind === "secret"

/* -------------------------------------------------------------------------- */
/* Notifications                                                              */
/* -------------------------------------------------------------------------- */

/**
 * A deliberately narrower set than the internal `ToastVariant`. `loader` is
 * excluded: it renders a spinner that stays until dismissed by id, and the
 * plugin protocol has no dismiss message — a plugin could pin one forever.
 */
export type NotifyVariant = "default" | "success" | "warning" | "error"

export const NOTIFY_VARIANTS: readonly NotifyVariant[] = [
	"default",
	"success",
	"warning",
	"error",
]

export type NotifyPayload = {
	title: string
	body?: string
	variant?: NotifyVariant
}

/**
 * Host-side clamps. They live here rather than in the renderer so the SDK can
 * document the same numbers to authors, and so a change moves both at once.
 */
export const NOTIFY_MAX_TITLE_LENGTH = 120
export const NOTIFY_MAX_BODY_LENGTH = 300
/** Auto-dismiss, in ms. A plugin cannot choose its own. */
export const NOTIFY_DURATION_MS = 5_000
/** Minimum gap between two accepted toasts from one plugin instance. */
export const NOTIFY_MIN_INTERVAL_MS = 1_000

/* -------------------------------------------------------------------------- */
/* Modal size                                                                 */
/* -------------------------------------------------------------------------- */

/** The payload of {@link MSG_MODAL_SIZE}. Every bound is optional, in CSS pixels. */
export type ModalSizeBounds = {
	minWidthPx?: number
	maxWidthPx?: number
	minHeightPx?: number
	maxHeightPx?: number
}

/** The largest bound either end accepts. Anything above is dropped, not clamped. */
export const MODAL_SIZE_MAX_PX = 10_000

/* -------------------------------------------------------------------------- */
/* Plugin metadata                                                            */
/* -------------------------------------------------------------------------- */

/**
 * The payload of {@link MSG_PLUGIN_METADATA}. Every field is optional, and an
 * empty object means "nothing constrains me".
 */
export type PluginMetadata = {
	/**
	 * The Context Types this plugin works in — ANY of them will do. Absent or
	 * `null` means no requirements: anywhere, the apps menu included. A list
	 * must not be empty (an empty one is refused, never read as anywhere), and
	 * cannot name `none`: only a plugin with no requirements goes where there
	 * is no object.
	 */
	supportedContextTypes?: PluginObjectType[] | null
	/** @deprecated Use `supportedContextTypes`. Read as it, when it is absent. */
	allowedObjectTypes?: PluginObjectType[]
	/** The narrowest column it is usable in, in CSS pixels. */
	minWidthPx?: number
	/** The shortest box it is usable in, in CSS pixels. */
	minHeightPx?: number
}

/** The largest dimension either end accepts. Anything above is dropped, not clamped. */
export const PLUGIN_METADATA_MAX_PX = 10_000

/** The object types a plugin may name, in the order the host lists them. */
export const PLUGIN_OBJECT_TYPES: readonly PluginObjectType[] = [
	"deal",
	"company",
	"person",
	"space",
]

/* -------------------------------------------------------------------------- */
/* Envelope                                                                   */
/* -------------------------------------------------------------------------- */

export type PluginMessage =
	| { type: typeof MSG_READY; payload?: ReadyPayload }
	| { type: typeof MSG_MODE; payload: ModePayload }
	| { type: typeof MSG_CONTEXT; payload: ContextPayload }
	| { type: typeof MSG_CONFIG; payload: ConfigPayload }
	| {
			type: typeof MSG_REQUEST_API_CREDENTIALS
			payload: ApiCredentialsRequestPayload
	  }
	| { type: typeof MSG_API_CREDENTIALS; payload: ApiCredentialsPayload }
	| {
			type: typeof MSG_REQUEST_SESSION_TOKEN
			payload: SessionTokenRequestPayload
	  }
	| { type: typeof MSG_SESSION_TOKEN; payload: SessionTokenPayload }
	| { type: typeof MSG_PREFERENCES; payload: PreferencesPayload }
	| { type: typeof MSG_REQUEST_CONFIG_FORM }
	| { type: typeof MSG_CONFIG_FORM; payload: ConfigFormPayload }
	| { type: typeof MSG_NOTIFY; payload: NotifyPayload }
	| { type: typeof MSG_MODAL_SIZE; payload: ModalSizeBounds }

/* -------------------------------------------------------------------------- */
/* Iframe hardening                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Shared by every host-side embedder of a plugin (the slot renderer, the admin
 * live preview, and the hidden config-form probe) so hardening cannot be
 * tightened in one place and left stale in another.
 *
 * `allow-same-origin` is intentionally KEPT so the plugin retains its own
 * origin — cookies/storage and the origin-based postMessage protocol depend on
 * it. That is only safe because plugins are always third-party origins, which
 * every embedder must enforce with `isHostOrigin`.
 *
 * WRC-2894 added `allow-popups` and `allow-downloads`: a plugin that links out
 * to its own application, or hands the user a generated file, could do neither.
 * A popup stays sandboxed — `allow-popups-to-escape-sandbox` is NOT granted.
 */
export const IFRAME_SANDBOX =
	"allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"

/**
 * Every browser capability a plugin does not need, denied without prompting.
 *
 * The microphone is the exception: it is delegated to every origin (`*`), so a
 * plugin can pass it on to frames it embeds. The browser still asks the user
 * before granting it. Meant to move into the plugin manifest, shown in the
 * gallery, rather than being granted to every plugin — see plugins-2 P2-26.
 */
export const IFRAME_ALLOW =
	"camera 'none'; microphone *; geolocation 'none'; payment 'none'; " +
	"usb 'none'; midi 'none'; display-capture 'none'; fullscreen 'none'; " +
	"autoplay 'none'; accelerometer 'none'; gyroscope 'none'; " +
	"magnetometer 'none'; clipboard-read 'none'"
